import { generateId } from '../utils/helpers';
import { ChatMessage, InitiativeData } from '../types/index';

export const createChatHandlers = (
  chatStep: number,
  setChatStep: React.Dispatch<React.SetStateAction<number>>,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setCurrentChatInput: React.Dispatch<React.SetStateAction<string>>,
  setInitiativeData: React.Dispatch<React.SetStateAction<InitiativeData>>,
  setShowThemeHelp: React.Dispatch<React.SetStateAction<boolean>>,
  fileInputRef: React.RefObject<HTMLInputElement>,
  initiativeData: InitiativeData,
  STRATEGIC_THEMES: any[]
) => {
  const generateFinalSummary = () => {
    const theme = initiativeData.theme || 'Uncategorized';
    const goal = initiativeData.goal || 'Goal not specified';
    const teams = initiativeData.userRoles.length > 0 ? initiativeData.userRoles.join(', ') : 'No teams specified';
    const files = initiativeData.files.length;
    const baseInitiativeName = goal.split(' ').slice(0, 4).join(' ') || 'New Initiative';
    
    return `🎉 **Initiative Summary**\n\n🎯 **Theme**: ${theme}\n📋 **Goal**: ${goal}\n👥 **Teams**: ${teams}\n📎 **Attachments**: ${files} file${files !== 1 ? 's' : ''}\n\n🚀 **Auto-Generated Features**:\n• ${baseInitiativeName} Fea 01 - Core functionality\n• ${baseInitiativeName} Fea 02 - Data integration\n• ${baseInitiativeName} Fea 03 - Security implementation\n• ${baseInitiativeName} Fea 04 - Analytics and reporting\n\n✅ Ready to create this initiative with features?`;
  };

  const handleSendChatMessage = () => {
    // This function would be implemented but needs access to current chat input
    // For now, return empty function that doesn't break the app
    console.log('Chat message handler called');
  };

  const handleChatFlow = (userInput: string) => {
    let aiResponse = '';
    
    switch (chatStep) {
      case 0:
        aiResponse = "Which **Strategic Theme** aligns with this initiative? You can select from the options below or type 'examples' for more details.\n\n✨ Available themes: **Growth**, **Efficiency**, **Compliance**, **Innovation**, **Customer Experience**";
        setChatStep(1);
        break;
        
      case 1:
        if (userInput.toLowerCase() === 'examples') {
          setShowThemeHelp(true);
          aiResponse = "Here are detailed theme examples:\n\n" + 
            STRATEGIC_THEMES.map(theme => 
              `🎯 **${theme.name}**\n${theme.description}\n*Examples: ${theme.examples.join(', ')}*`
            ).join('\n\n') + 
            "\n\nWhich theme best fits your initiative?";
        } else {
          const theme = STRATEGIC_THEMES.find(t => 
            t.name.toLowerCase().includes(userInput.toLowerCase()) ||
            userInput.toLowerCase().includes(t.name.toLowerCase())
          );
          
          if (theme) {
            setInitiativeData(prev => ({ ...prev, theme: theme.name }));
            aiResponse = `Perfect! **${theme.name}** theme selected. 🎯\n\nNow, please describe the **primary goal** for this ${theme.name} initiative. What specific outcome are you trying to achieve?`;
            setChatStep(2);
          } else if (userInput.toLowerCase() === 'skip') {
            setInitiativeData(prev => ({ ...prev, theme: 'Uncategorized' }));
            aiResponse = "Theme set to 'Uncategorized' (you can update this later). Now, please describe the **primary goal** for this initiative:";
            setChatStep(2);
          } else {
            aiResponse = "I didn't recognize that theme. Please choose from: **Growth**, **Efficiency**, **Compliance**, **Innovation**, **Customer Experience**, or type 'examples' for more details.";
          }
        }
        break;
        
      case 2:
        setInitiativeData(prev => ({ ...prev, goal: userInput }));
        aiResponse = `Excellent goal! 🎯 "${userInput}"\n\nWhich **teams** will be impacted by this initiative? You can select from the suggestions below or type team names separated by commas.`;
        setChatStep(3);
        break;
        
      case 3:
        const roles = userInput.split(',').map(r => r.trim()).filter(r => r);
        setInitiativeData(prev => ({ ...prev, userRoles: roles }));
        aiResponse = `Great! Teams selected: **${roles.join(', ')}** 👥\n\nFinally, do you have any **supporting files** to attach? You can drag files here, click the upload area below, or type 'skip' to continue without files.\n\n🎯 **Next**: I'll create 4 starter features to help organize your work across the SDLC phases.`;
        setChatStep(4);
        break;
        
      case 4:
        if (userInput.toLowerCase() === 'browse') {
          fileInputRef.current?.click();
          aiResponse = "File browser opened! 📁 Select your files and I'll show a summary when ready.";
        } else if (userInput.toLowerCase() === 'skip') {
          aiResponse = generateFinalSummary();
          setChatStep(5);
        } else {
          aiResponse = "You can drag files to the upload area below, type 'browse' to select files, or 'skip' to continue without attachments.\n\n💡 **Note**: I'll automatically create 4 starter features for your initiative to help you get organized quickly!";
        }
        break;
        
      default:
        break;
    }
    
    if (aiResponse) {
      const aiMessage: ChatMessage = {
        id: generateId(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, aiMessage]);
    }
  };

  const handleChatKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendChatMessage();
    }
  };

  const handleCreateInitiative = () => {
    // This would typically create the actual initiative
    // For now, just log that it was called
    console.log('Create initiative handler called', initiativeData);
  };

  return {
    generateFinalSummary,
    handleSendChatMessage,
    handleChatFlow,
    handleChatKeyPress,
    handleCreateInitiative
  };
};